import type { CSSProperties, FC } from "react";

interface KeyModeSelectorProps {
  keyMode: "customer-managed" | "verimatrix-managed";
  onKeyModeChange: (mode: "customer-managed" | "verimatrix-managed") => void;
  certificate: string;
  onCertificateChange: (value: string) => void;
  privateKey: string;
  onPrivateKeyChange: (value: string) => void;
}

const optionCardBase: CSSProperties = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: 4,
  padding: "14px 16px",
  borderRadius: "var(--vmx-radius)",
  border: "1.5px solid var(--vmx-gray-200)",
  cursor: "pointer",
  transition: "all 150ms cubic-bezier(0.4, 0, 0.2, 1)",
  background: "var(--vmx-white)",
};

const optionCardSelected: CSSProperties = {
  ...optionCardBase,
  borderColor: "var(--vmx-accent)",
  background: "rgba(0, 180, 216, 0.04)",
  boxShadow: "0 0 0 3px rgba(0, 180, 216, 0.10)",
};

const optionTitle: CSSProperties = {
  fontSize: "0.8125rem",
  fontWeight: 600,
  color: "var(--vmx-gray-800)",
};

const optionDesc: CSSProperties = {
  fontSize: "0.7rem",
  color: "var(--vmx-gray-500)",
  lineHeight: 1.4,
};

const radioStyle: CSSProperties = {
  accentColor: "var(--vmx-accent)",
  width: 16,
  height: 16,
  margin: 0,
  flexShrink: 0,
};

export const KeyModeSelector: FC<KeyModeSelectorProps> = ({
  keyMode,
  onKeyModeChange,
  certificate,
  onCertificateChange,
  privateKey,
  onPrivateKeyChange,
}) => {
  return (
    <div className="vmx-form-group">
      <label className="vmx-label">Key Management Mode</label>
      <div style={{ display: "flex", gap: 12 }}>
        <label
          style={
            keyMode === "verimatrix-managed"
              ? optionCardSelected
              : optionCardBase
          }
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <input
              type="radio"
              name="keyMode"
              value="verimatrix-managed"
              checked={keyMode === "verimatrix-managed"}
              onChange={() => onKeyModeChange("verimatrix-managed")}
              style={radioStyle}
            />
            <span style={optionTitle}>Verimatrix-Managed</span>
          </div>
          <span style={{ ...optionDesc, paddingLeft: 26 }}>
            Keys are generated and stored securely by Verimatrix
          </span>
        </label>
        <label
          style={
            keyMode === "customer-managed" ? optionCardSelected : optionCardBase
          }
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <input
              type="radio"
              name="keyMode"
              value="customer-managed"
              checked={keyMode === "customer-managed"}
              onChange={() => onKeyModeChange("customer-managed")}
              style={radioStyle}
            />
            <span style={optionTitle}>Customer-Managed</span>
          </div>
          <span style={{ ...optionDesc, paddingLeft: 26 }}>
            You provide your own certificate and private key
          </span>
        </label>
      </div>
      {keyMode === "customer-managed" && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 12,
            marginTop: 8,
          }}
        >
          <div className="vmx-form-group">
            <label className="vmx-label">Certificate (PEM)</label>
            <textarea
              className="vmx-input"
              value={certificate}
              onChange={(e) => onCertificateChange(e.target.value)}
              placeholder={
                "-----BEGIN CERTIFICATE-----\n...\n-----END CERTIFICATE-----"
              }
              style={{
                minHeight: 120,
                fontFamily: "var(--vmx-font-mono)",
                fontSize: "0.7rem",
                resize: "vertical",
              }}
            />
          </div>
          <div className="vmx-form-group">
            <label className="vmx-label">Private Key (PEM)</label>
            <textarea
              className="vmx-input"
              value={privateKey}
              onChange={(e) => onPrivateKeyChange(e.target.value)}
              placeholder={
                "-----BEGIN PRIVATE KEY-----\n...\n-----END PRIVATE KEY-----"
              }
              style={{
                minHeight: 120,
                fontFamily: "var(--vmx-font-mono)",
                fontSize: "0.7rem",
                resize: "vertical",
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};
